/**
 * @author Artem Nakhodkin <artem.nakhodkin@racingpost.com>
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import rpMqBreakpoints, { breakpointCodes } from 'rp-mq-breakpoints';

import NewsStory from './../NewsStory';
import Advertisement from './../../../Advertisement';

const ADVERTISEMENT = 'advertisement';

/**
 * Component that is used for rendering <NewsStories />
 * @param storiesList
 * @param adPositions
 * @param advertisement
 */

class NewsStories extends Component {

    static get defaultProps() {
        return {
            adPositions: [],
            newsTitle: '',
            tag: '',
        };
    }

    static propTypes = {
        storiesList: PropTypes.arrayOf(
            /**
             * List of news
             */
            PropTypes.shape({
                /**
                 * News id
                 */
                id: PropTypes.oneOfType([
                    PropTypes.number,
                    PropTypes.string,
                ]).isRequired,
                /**
                 * News url
                 */
                url: PropTypes.string,
                /**
                 * News image
                 */
                image: PropTypes.object,
                /**
                 * News could be free or not
                 */
                isFree: PropTypes.bool.isRequired,
                /**
                 * News could be live blog or not
                 */
                isLive: PropTypes.bool,
                /**
                 * News sections
                 */
                section: PropTypes.string,
                /**
                 * News label
                 */
                label: PropTypes.string,
                /**
                 * News with video
                 */
                videoIncluded: PropTypes.bool,
                /**
                 * News with audio
                 */
                audioIncluded: PropTypes.bool,
                /**
                 * News headline
                 */
                headline: PropTypes.string,
            })

        ).isRequired,
        /**
         * List of available positions in the news section
         */
        adPositions: PropTypes.arrayOf(
            PropTypes.shape({
                /**
                 * Position in news section
                 */
                ad: PropTypes.number.isRequired,
                /**
                 * Advertising position
                 */
                position: PropTypes.number.isRequired,
            })
        ),

        advertisement: PropTypes.arrayOf(
            PropTypes.shape({
                /**
                 * Advertisement position
                 * Full list of available positions you can find there:
                 * https://docs.google.com/spreadsheets/d/1A3s9YjyVhmBcrh5kMl8i2P3RJEyAcUhmwwthISf4gl8/edit#gid=834985875
                 */
                position: PropTypes.number.isRequired,
                /**
                 * Advertisement className
                 * Additional css-classes, applied to the component
                 */
                className: PropTypes.string,
                /**
                 * Advertisement isActive
                 * Define, if Advertisement block should be shown
                 */
                isActive: PropTypes.bool.isRequired,
            })
        ).isRequired,
        newsTitle: PropTypes.string
    };

    state = {
        currentBreakpointCode: rpMqBreakpoints.current().code,
    };

    componentDidMount() {
        window.addEventListener('rpBreakpointsSwitch', this.setCurrentBreakpointCode);
    }

    componentWillUnmount() {
        window.removeEventListener('rpBreakpointsSwitch', this.setCurrentBreakpointCode);
    }

    setCurrentBreakpointCode = (event) => {
        const code = event.detail.breakpoint.code;
        this.setState({
            currentBreakpointCode: code,
        });
    };

    /**
     * Method, used for merging news with advertisements
     * @returns {array}
     */
    mergeNewsWithAds() {
        const { storiesList, adPositions, advertisement } = this.props;
        const mergedStories = storiesList.slice();

        adPositions.forEach((adPosition) => {
            if (advertisement.find(ad =>
                ad.position === adPosition.position && ad.isActive)) {
                const currentBreakpointCode = this.state.currentBreakpointCode;
                const isCentered = currentBreakpointCode === breakpointCodes.EXTRA_NARROW;
                const hidden = !adPosition.mq.includes(currentBreakpointCode);
                if (!hidden) {
                    mergedStories
                        .splice(hidden ? adPosition.ad : adPosition.ad - 1, 0, {
                            type: ADVERTISEMENT,
                            position: adPosition.position,
                            hidden,
                            isCentered,
                        });
                }
            }
        });

        return mergedStories;
    }

    /**
     * Method, used for render news with advertisements
     * @returns {array}
     */
    renderNews() {
        const mergedStories = this.mergeNewsWithAds();
        let hiddenCounter = 0;

        return mergedStories.map((story, index) => {
            const { hidden, position, isCentered } = story;

            if (hidden || position === 30) {
                hiddenCounter += 1;
            }
            const realIndex = index - hiddenCounter;
            const odd = (realIndex + 1) % 2 ? 'odd' : 'even';

            if (story.type === ADVERTISEMENT) {
                return (
                    <Advertisement
                        position={position}
                        key={position}
                        className={`rh-newsStory ${hidden ? 'hidden' : odd}`}
                        isCentered={isCentered}
                    />);
            }

            return <NewsStory story={story} key={story.id} className={odd} />;
        });
    }

    render() {
        const Heading = this.props.newsTitle ? 'h1' : 'h2';
        return (
            <section className="rh-news" data-test-selector="section-news">
                <Heading className="rh-newsHeader">
                    {this.props.newsTitle || 'Today\'s highlights'}
                </Heading>
                <div className="rh-newsArticles">
                    {this.renderNews()}
                </div>
                <div className="rh-newsButton__wrapper">
                    <a href="/news/" className="rh-newsButton ui-btn ui-btn_secondary" id="fullNewsServiceBtn">
                        <span className="rh-newsButton__label">Full news service</span>
                    </a>
                </div>
            </section>
        );
    }
}

export { NewsStories as default };



// WEBPACK FOOTER //
// ./src/js/News/components/NewsStories/NewsStories.js